import type { Attributes, Context, Link } from "@opentelemetry/api";
import { SpanKind } from "@opentelemetry/api";
import { CompositePropagator, W3CTraceContextPropagator } from "@opentelemetry/core";
import { OTLPTraceExporter } from "@opentelemetry/exporter-trace-otlp-http";
import { registerInstrumentations } from "@opentelemetry/instrumentation";
import { FetchInstrumentation } from "@opentelemetry/instrumentation-fetch";
import { XMLHttpRequestInstrumentation } from "@opentelemetry/instrumentation-xml-http-request";
import { Resource } from "@opentelemetry/resources";
import type { Sampler, SamplingResult } from "@opentelemetry/sdk-trace-base";
import { AlwaysOnSampler, SamplingDecision } from "@opentelemetry/sdk-trace-base";
import { BatchSpanProcessor, WebTracerProvider } from "@opentelemetry/sdk-trace-web";
import { ATTR_SERVICE_NAME } from "@opentelemetry/semantic-conventions";

export interface ClientTelemetryConfig {
  serviceName: string;
  otlpEndpoint?: string;
  propagateTraceHeaderCorsUrls?: (string | RegExp)[];
}

let provider: WebTracerProvider | null = null;

// -----------------------------------------------------------------------
// Client-side noise filter, applied at the SAMPLER level.
//
// ignoreUrls on FetchInstrumentation/XMLHttpRequestInstrumentation has
// repeatedly failed to filter known third-party noise (Google Ads,
// Analytics, GTM subdomains) even with correct, verified-matching regex
// patterns and confirmed full server restarts. Rather than keep chasing
// why ignoreUrls' internal matching isn't applying, this sampler checks
// the span's own recorded "http.url" ATTRIBUTE after the span is created —
// a completely separate code path from ignoreUrls' pre-creation URL check,
// and one we control directly rather than depending on the instrumentation
// library's internal string-matching behavior.
const CLIENT_NOISE_URL_PATTERNS = [
  /(^|\.)google\.com/, // catches www.google.com, analytics.google.com, ads.google.com, etc.
  /google-analytics\.com/,
  /googleadservices\.com/,
  /googletagmanager\.com/,
  /googlesyndication\.com/,
  /doubleclick\.net/,
  /clarity\.ms/,
  /marker\.io/,
];

class ClientFilterSampler implements Sampler {
  private _base: Sampler;

  constructor(base?: Sampler) {
    this._base = base || new AlwaysOnSampler();
  }

  shouldSample(
    context: Context,
    traceId: string,
    spanName: string,
    spanKind: SpanKind,
    attributes: Attributes,
    links: Link[],
  ): SamplingResult {
    // TEMP DIAGNOSTIC — logs for EVERY span, any kind, unconditionally.
    // If this never appears at all in the console, the sampler is not
    // wired into whatever tracer is actually creating spans in this app.
    console.log("[ClientFilterSampler] shouldSample called", { spanName, spanKind });

    if (spanKind === SpanKind.CLIENT) {
      const url = (attributes?.["http.url"] || "") as string;
      const matched = url && CLIENT_NOISE_URL_PATTERNS.some((pattern) => pattern.test(url));
      console.log("[ClientFilterSampler] CLIENT span check", { spanName, url, matched });
      if (matched) {
        return { decision: SamplingDecision.NOT_RECORD };
      }
    }

    return this._base.shouldSample(context, traceId, spanName, spanKind, attributes, links);
  }

  toString(): string {
    return `ClientFilterSampler(${this._base.toString()})`;
  }
}

// Guards against overlapping forceFlush() calls when multiple unload-related
// events (pagehide, visibilitychange) fire close together during the same
// navigation. forceFlush() is safe to call repeatedly (it's a no-op on an
// empty queue), but we don't want two overlapping in-flight flush promises.
let flushInFlight = false;

function flushTelemetry() {
  if (flushInFlight || !provider) return;
  flushInFlight = true;
  provider
    .forceFlush()
    .catch(() => {
      // Best-effort only. The page may already be tearing down; nothing
      // useful can be done with a failure here.
    })
    .finally(() => {
      flushInFlight = false;
    });
}

function registerFlushListeners() {
  if (typeof window === "undefined") return;

  // Fires reliably on hard navigations (including cross-origin, e.g. moving
  // from this app to another app in the monorepo on a different port/domain),
  // tab close, and back/forward navigation. This is the primary safety net.
  window.addEventListener("pagehide", flushTelemetry);

  // Fires when the tab is backgrounded or the page becomes hidden (mobile
  // app-switch, tab change). On many browsers this also fires *before*
  // pagehide during a real navigation, so it acts as an earlier attempt.
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") {
      flushTelemetry();
    }
  });

  // If the page is restored from the back/forward cache (bfcache), make sure
  // a subsequent navigation away from it still triggers a flush correctly.
  window.addEventListener("pageshow", (event: PageTransitionEvent) => {
    if (event.persisted) {
      flushTelemetry();
    }
  });
}

export function initClientTelemetry(config: ClientTelemetryConfig) {
  if (typeof window === "undefined") return;

  const enableTracing = process.env.NEXT_PUBLIC_ENABLE_TRACING;
  if (enableTracing !== "true" && enableTracing !== "1") {
    console.log("[Telemetry Client] Tracing is disabled via NEXT_PUBLIC_ENABLE_TRACING env");
    return;
  }

  if (provider) {
    console.log("[Telemetry Client] Web Telemetry already initialized");
    return;
  }

  const environment = process.env.NEXT_PUBLIC_ENVIRONMENT ?? process.env.ENVIRONMENT;
  const rawServiceName = config.serviceName;
  const serviceName =
    environment && !rawServiceName.endsWith(`-${environment}`)
      ? `${rawServiceName}-${environment}`
      : rawServiceName;

  console.log(`[Telemetry Client] Initializing Client Tracing for ${serviceName}...`);
  console.log(
    "BUILD_MARKER_CHECK_9f8a2b — if you don't see this exact string in the browser console, the browser is NOT running this file at all.",
  );

  provider = new WebTracerProvider({
    resource: Resource.default().merge(
      new Resource({
        [ATTR_SERVICE_NAME]: serviceName,
      }),
    ),
    sampler: new ClientFilterSampler(),
  });

  // Client-side traces are sent to configured OTLP endpoint or reverse proxy endpoint
  let traceUrl =
    config.otlpEndpoint ??
    process.env.NEXT_PUBLIC_OTEL_EXPORTER_OTLP_ENDPOINT ??
    "/booking/api/v1/telemetry/traces";
  if (traceUrl.startsWith("/")) {
    traceUrl = `${window.location.origin}${traceUrl}`;
  }
  console.log(`[Telemetry Client] Exporting client traces to: ${traceUrl}`);

  const exporter = new OTLPTraceExporter({
    url: traceUrl,
  });

  // scheduledDelayMillis is lowered from the SDK default (5000ms) to reduce
  // how long spans can sit unflushed in the worst case. This does NOT
  // increase request volume during idle periods — the processor only ever
  // exports when spans are actually queued, so a shorter delay just means a
  // smaller window of exposure to the unload race, not periodic polling.
  provider.addSpanProcessor(
    new BatchSpanProcessor(exporter, {
      scheduledDelayMillis: 2000,
      maxExportBatchSize: 100,
      maxQueueSize: 1000,
      exportTimeoutMillis: 5000,
    }),
  );

  // Register the provider globally
  provider.register({
    propagator: new CompositePropagator({
      propagators: [new W3CTraceContextPropagator()],
    }),
  });
  console.log("[Telemetry Client] W3C propagator registered");

  registerFlushListeners();
  console.log("[Telemetry Client] Flush-on-unload listeners registered");

  // Configure trace header propagation to BFF / current host
  const host = window.location.host;
  const propagateUrls = config.propagateTraceHeaderCorsUrls ?? [
    new RegExp(`^https?://${host}`),
    /^\//, // relative urls
    /.*\/bff\/api\/.*/, // BFF URL patterns
  ];

  // Kept as a first-line, cheaper filter (avoids even creating a span for
  // these where it does work), with ClientFilterSampler above as the
  // authoritative backstop that doesn't depend on this matching correctly.
  const ignoreUrls = [
    /\/booking\/api\/v1\/telemetry\/traces/,
    /\/api\/v1\/telemetry\/traces/,
    /\/telemetry\/traces/,
    /\/receiver\/v1\/trace/,
    /sumologic\.com/,
    /\/__nextjs/,
    /\/_next\//,
    /clarity\.ms/,
    /google-analytics\.com/,
    /doubleclick\.net/,
    /google\.com\/recaptcha/,
    /(^|\.)google\.com/,
    /googleadservices\.com/,
    /googletagmanager\.com/,
    /googlesyndication\.com/,
    /marker\.io/,
    /\.(ico|png|jpg|jpeg|gif|svg|css|js|woff2?|ttf|otf|eot|map)$/i,
  ];

  registerInstrumentations({
    instrumentations: [
      new FetchInstrumentation({
        propagateTraceHeaderCorsUrls: propagateUrls,
        clearTimingResources: true,
        ignoreUrls,
      }),
      new XMLHttpRequestInstrumentation({
        propagateTraceHeaderCorsUrls: propagateUrls,
        ignoreUrls,
      }),
    ],
  });

  console.log(`[Telemetry Client] Client Tracing started successfully`);
}
